/**
 * BFF·커리큘럼의 시나리오 id가 `scenarioDetails` 키에 없을 때(온라인 id 181 등)
 * `contentCode` / 스튜디오 `key`로 스프레드시트·커리큘럼에 정의된 동일 유형을 찾는다.
 */
import { scenarioDetails, type ScenarioDetail } from '@/data/scenarioDetails';

const detailsById = scenarioDetails as Record<number, ScenarioDetail | undefined>;
const allDetails = () => Object.values(scenarioDetails).filter(Boolean) as ScenarioDetail[];

/**
 * api2/플랫폼이 `{ "reload": true }` 만 줄 때(세션/데이터 미스매치 등) — 실질 payload 없음.
 */
export function isPlatformReloadOnlyPayload(data: unknown): boolean {
	if (data == null) return true;
	if (typeof data !== 'object') return false;
	const o = data as Record<string, unknown>;
	const k = Object.keys(o);
	if (k.length === 0) return true;
	if (k.length === 1 && o['reload'] === true) return true;
	const inner = o['data'] ?? o['result'] ?? o['Data'];
	if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
		const d = inner as Record<string, unknown>;
		const k2 = Object.keys(d);
		if (k2.length === 1 && d['reload'] === true) return true;
	}
	return false;
}

function normalizeKey(raw: string | null | undefined): string {
	return String(raw ?? '')
		.trim()
		.toLowerCase();
}

/**
 * 1) id로 직접, 2) `sessionItem.contentCode` + `bffContentKey(스튜디오 /info key)` 로 동일 `contentCode` 를 가진 엔트리, 3) `scenarioDetails[1]`.
 */
export function resolveScenarioDetailForSession(
	sessionId: string,
	sessionItem: { name?: string; id?: string | number; contentCode?: string } | null | undefined,
	opts?: { bffContentKey?: string | null }
): ScenarioDetail {
	const n = Number.parseInt(String(sessionId), 10);
	if (Number.isFinite(n) && n > 0) {
		const d = detailsById[n];
		if (d) return d;
	}

	const hint =
		normalizeKey(sessionItem?.contentCode) ||
		normalizeKey(opts?.bffContentKey) ||
		'';
	if (hint !== '') {
		const row = allDetails().find(
			(d) => d.contentCode != null && normalizeKey(d.contentCode) === hint
		);
		if (row) {
			return {
				...row,
				id: Number.isFinite(n) && n > 0 ? n : row.id,
				...(sessionItem?.name && String(sessionItem.name).trim() !== ''
					? { title: String(sessionItem.name) }
					: {}),
			};
		}
	}
	return detailsById[1] as ScenarioDetail;
}
